import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../../common/user";
import { globalBetListService } from "../../../../services/admin/globalBetList.service";
import { setGeneralProgressHide, setGeneralProgressShow } from "../../../common/ui";

export type payloadGlobalBetList = {
  playerCurrency: boolean;
  fullAccess: boolean;
  payload: {
    dateFrom: string;
    dateTo: string;
    filter?: {
      betId?: string;
      platformIds?: string[] | number[];
      brands?: string[] | number[];
      betTypes?: string[] | number[];
      betStatuses?: string[] | number[];
      betResults?: string[] | number[];
      sports?: string[] | number[];
      playerId?: string;
      agentIds?: string[] | number[];
      underAgentId?: number | string;
    };
    page?: number;
    perPage?: number;
    order?: {
      acceptTime?: string;
    };
  }
};

export type payloadBetDetails = {
  betId: string;
  playerId: string;
  date: string;
}

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
  show?: boolean;
  isRecalculatable?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
  totalEntries: number;
  startRowNum: number;
  endRowNum: number;
  totalPages: number;
}

export type globalBetListSliceState = {
  list: {
    data: ITableData | null;
    error: any;
    isFetching: boolean;
  };
  betDetails: {
    data: any;
    error: any;
    isFetching: boolean;
  };
  download: {
    data: any;
    error: any;
    isFetching: boolean;
  };
  options: {
    data: any;
    error: any;
    isFetching: boolean;
  };
};

const initialState: globalBetListSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  betDetails: {
    data: null,
    error: null,
    isFetching: false,
  },
  download: {
    data: null,
    error: null,
    isFetching: false,
  },
  options: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getGlobalBetListData: any = createAsyncThunk(
  'agents/globalBetList/getGlobalBetListData',
  async (payload: payloadGlobalBetList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await globalBetListService.getGlobalBetList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getBetDetailsData: any = createAsyncThunk(
  'agents/globalBetList/getBetDetailsData',
  async (payload: payloadBetDetails, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await globalBetListService.getBetDetails(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const downloadGlobalBetListData: any = createAsyncThunk(
  'agents/globalBetList/downloadGlobalBetListData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await globalBetListService.downloadGlobalBetList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getGlobalBetListOptionsData: any = createAsyncThunk(
  'agents/globalBetList/getGlobalBetListOptionsData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await globalBetListService.getGlobalBetListOptions();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const globalBetListSlice = createSlice({
  name: 'globalBetList',
  initialState: initialState,
  reducers: {
    clearGlobalBetListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearBetDetailsState(state) {
      state.betDetails.error = null;
      state.betDetails.isFetching = false;
      state.betDetails.data = null;
    },
    clearDownloadState(state) {
      state.download.error = null;
      state.download.isFetching = false;
      state.download.data = null;
    },
    clearOptionsState(state) {
      state.options.error = null;
      state.options.isFetching = false;
      state.options.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<globalBetListSliceState>>) => {
    builder.addCase(getGlobalBetListData.pending, (state) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getGlobalBetListData.fulfilled, (state, action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getGlobalBetListData.rejected, (state, action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });
    builder.addCase(getBetDetailsData.pending, (state) => {
      state.betDetails.isFetching = true;
      state.betDetails.error = null;
    });
    builder.addCase(getBetDetailsData.fulfilled, (state, action) => {
      state.betDetails.error = null;
      state.betDetails.isFetching = false;
      state.betDetails.data = action.payload;
    });
    builder.addCase(getBetDetailsData.rejected, (state, action) => {
      state.betDetails.isFetching = false;
      state.betDetails.error = action.payload;
      state.betDetails.data = null;
    });
    builder.addCase(downloadGlobalBetListData.pending, (state) => {
      state.download.isFetching = true;
      state.download.error = null;
    });
    builder.addCase(downloadGlobalBetListData.fulfilled, (state, action) => {
      state.download.error = null;
      state.download.isFetching = false;
      state.download.data = action.payload;
    });
    builder.addCase(downloadGlobalBetListData.rejected, (state, action) => {
      state.download.isFetching = false;
      state.download.error = action.payload;
      state.download.data = null;
    });
    builder.addCase(getGlobalBetListOptionsData.pending, (state) => {
      state.options.isFetching = true;
      state.options.error = null;
    });
    builder.addCase(getGlobalBetListOptionsData.fulfilled, (state, action) => {
      state.options.error = null;
      state.options.isFetching = false;
      state.options.data = action.payload;
    });
    builder.addCase(getGlobalBetListOptionsData.rejected, (state, action) => {
      state.options.isFetching = false;
      state.options.error = action.payload;
      state.options.data = null;
    });
  },
});

export const {
  clearGlobalBetListState,
  clearBetDetailsState,
  clearDownloadState,
  clearOptionsState,
} = globalBetListSlice.actions;

export default globalBetListSlice.reducer;
